// ===========================================
// CPG HELPERS — shared by Accounts, Products and the Customers tab
// ===========================================
// Small pure functions: SKU + pack-size parsing (mirrors the API's sizeFrom in
// routes/hub.ts), the legacy "Category: X · …" notes convention, money/date
// formatting, and the sales-category chip colors. No fetching here.

export const fmtMoney = (c: number) => `$${((c || 0) / 100).toFixed(2)}`;
// Whole dollars for summary numbers — $12,480 reads faster than $12,480.00
export const fmtMoneyShort = (c: number) => `$${Math.round((c || 0) / 100).toLocaleString('en-US')}`;

export const fmtDay = (v?: string | Date | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
export const fmtDayYear = (v?: string | Date | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// "today" / "3d ago" / "2mo ago" — friendly recency, never a raw timestamp
export function timeAgo(v?: string | Date | null): string {
  if (!v) return '—';
  const days = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

export const daysSince = (v: string | Date) => Math.floor((Date.now() - new Date(v).getTime()) / 86400000);

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// "Net 30" → 30. null when the terms don't carry a number (or aren't set).
export function netDaysFrom(paymentTerms?: string | null): number | null {
  const m = /(\d+)/.exec(String(paymentTerms || ''));
  return m ? parseInt(m[1], 10) : null;
}

// ---- Deduction reason codes (mirrors the API's REASONS list; labels match
// the Deductions tab so the same code never reads two ways).
export const DEDUCTION_REASON_LABELS: Record<string, string> = {
  promo: 'Promo', advertising: 'Co-op advertising', slotting: 'Slotting', samples: 'Samples',
  catalog: 'Catalog fee', chargeback: 'Chargeback (MCB)', freight: 'Freight',
  shortpay: 'Short-pay', out_of_code: 'Out of code', spoilage: 'Spoilage', other: 'Unexplained',
};
export const deductionReason = (code?: string | null) => DEDUCTION_REASON_LABELS[code || ''] || code || 'Unexplained';

// ---- CPG detection: the exact signals Layout.tsx keys off — the business
// kind, or the CPG-only settings blobs an account could only carry as a brand.
export function isCpgSettings(rs: any): boolean {
  const s = rs || {};
  return s.businessType === 'cpg' || s.businessKind === 'cpg' || !!s.supplyRatios || !!s.channelForecast;
}

// ---- Sales categories (mirrors the API enum; colors are ours)
export const CATEGORY_OPTIONS: Array<[string, string]> = [
  ['retail', 'Retail'], ['ecommerce', 'E-commerce'], ['food_service', 'Food service'], ['distribution', 'Distribution'],
];
export const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(CATEGORY_OPTIONS);
export const CATEGORY_CHIP: Record<string, string> = {
  retail: 'bg-teal-50 text-teal-700',
  ecommerce: 'bg-violet-50 text-violet-700',
  food_service: 'bg-amber-50 text-amber-700',
  distribution: 'bg-indigo-50 text-indigo-700',
};

// Legacy convention: imported customers carried the category in notes as
// "Category: Retail · Net 30 · …". The API already derives `category` from it;
// at render time we show the remainder only, so the same fact never reads twice.
export function displayNotes(notes?: string | null): string {
  const raw = String(notes || '').trim();
  if (!raw) return '';
  const m = /^\s*(?:category:\s*)?(retail|e-commerce|ecommerce|internal food service|wholesale & distribution)\s*(?:·\s*)?/i.exec(raw);
  return m ? raw.slice(m[0].length).trim() : raw;
}

// ---- SKU: lives embedded in the menu item's description as "SKU 0001"
export function parseSku(description?: string | null): string | null {
  const d = description || '';
  const labeled = /\bSKU[\s:#-]*([A-Za-z0-9][A-Za-z0-9-]*)/i.exec(d);
  if (labeled) return labeled[1];
  const bare = /(\d{3,6})/.exec(d); // same fallback ScanPOModal's skuLabel uses
  return bare ? bare[1] : null;
}

// ---- Pack size from a product name/SKU — mirror of the API's sizeFrom
// (routes/hub.ts): "12pk 250ml" → liters, "8 oz" → grams. null = no size parses.
export function sizeFrom(...texts: Array<string | null | undefined>): { liters?: number; grams?: number } | null {
  for (const t of texts) {
    if (!t) continue;
    const vol = /(\d+(?:[.,]\d+)?)\s*(ml|l|lt|ltr|liter|litre)s?\b/i.exec(t);
    if (vol) {
      const qty = parseFloat(vol[1].replace(',', '.'));
      if (Number.isFinite(qty) && qty > 0) return { liters: vol[2].toLowerCase() === 'ml' ? qty / 1000 : qty };
    }
    const wt = /(\d+(?:[.,]\d+)?)\s*(oz|ounce)s?\b/i.exec(t);
    if (wt) {
      const qty = parseFloat(wt[1].replace(',', '.'));
      if (Number.isFinite(qty) && qty > 0) return { grams: qty * 28.3495 };
    }
  }
  return null;
}

// Liters in one order-line unit, respecting pack counts when both appear:
// "Apple Juice 12pk 250ml" → 3 L per case. Without a pack count, the parsed
// size is the unit itself.
export function litersPerUnit(name?: string | null): number | null {
  const size = sizeFrom(name);
  if (!size?.liters) return null;
  const pack = /(\d+)\s*(?:pk|pack|x|ct|count)\b/i.exec(name || '');
  const n = pack ? parseInt(pack[1], 10) : 1;
  return Number.isFinite(n) && n > 1 ? size.liters * n : size.liters;
}
